export const roles = ["member", "organiser", "administrator", "superadmin"] as const;
export type Role = (typeof roles)[number];

export const roleLabels: Readonly<Record<Role, string>> = {
  member: "Member",
  organiser: "Organiser",
  administrator: "Administrator",
  superadmin: "Superadmin",
};

export interface Capabilities {
  readonly createAccountInvitations: boolean;
  readonly manageCanvassingSessions: boolean;
  readonly administerOrganization: boolean;
}

export interface Organization {
  readonly id: string;
  readonly name: string;
}

export interface UserIdentity {
  readonly id: string;
  readonly email: string;
  readonly displayName: string;
}

export interface Account {
  readonly user: UserIdentity;
  readonly organization: Organization;
  readonly role: Role;
  readonly capabilities: Capabilities;
  readonly accessVersion: number;
}

export interface AuthenticatedSession extends Account {
  readonly accessToken: string;
  readonly refreshToken: string;
  readonly expiresAt: Date;
}

export function parseSession(value: unknown): AuthenticatedSession {
  const record = objectValue(value, "session");
  const account = parseAccount(record);
  const expiresAtSeconds = integerField(record, "expires_at", 1);
  return {
    ...account,
    accessToken: nonEmptyStringField(record, "access_token"),
    refreshToken: nonEmptyStringField(record, "refresh_token"),
    expiresAt: new Date(expiresAtSeconds * 1_000),
  };
}

export function serializeSession(session: AuthenticatedSession): string {
  return JSON.stringify({
    access_token: session.accessToken,
    refresh_token: session.refreshToken,
    expires_at: Math.floor(session.expiresAt.getTime() / 1_000),
    organization: session.organization,
    user: {
      id: session.user.id,
      email: session.user.email,
      display_name: session.user.displayName,
    },
    role: session.role,
    capabilities: {
      create_account_invitations: session.capabilities.createAccountInvitations,
      manage_canvassing_sessions: session.capabilities.manageCanvassingSessions,
      administer_organization: session.capabilities.administerOrganization,
    },
    access_version: session.accessVersion,
  });
}

export function parseAccount(value: unknown): Account {
  const record = objectValue(value, "account");
  const role = roleField(record, "role");
  const capabilities = parseCapabilities(record.capabilities);
  if (!capabilitiesMatchRole(capabilities, role)) {
    throw new TypeError("account capabilities do not match its role");
  }
  return {
    user: parseUserIdentity(record.user),
    organization: parseOrganization(record.organization),
    role,
    capabilities,
    accessVersion: integerField(record, "access_version", 0),
  };
}

function parseCapabilities(value: unknown): Capabilities {
  const record = objectValue(value, "capabilities");
  return {
    createAccountInvitations: booleanField(record, "create_account_invitations"),
    manageCanvassingSessions: booleanField(record, "manage_canvassing_sessions"),
    administerOrganization: booleanField(record, "administer_organization"),
  };
}

function capabilitiesMatchRole(capabilities: Capabilities, role: Role): boolean {
  return (
    capabilities.createAccountInvitations === (role !== "member") &&
    capabilities.manageCanvassingSessions === (role !== "member") &&
    capabilities.administerOrganization === (role === "administrator" || role === "superadmin")
  );
}

function parseOrganization(value: unknown): Organization {
  const record = objectValue(value, "organization");
  const id = nonEmptyStringField(record, "id");
  if (id.length > 64 || id.trim() !== id) {
    throw new TypeError("organization has an invalid ID");
  }
  return { id, name: nonEmptyStringField(record, "name") };
}

function parseUserIdentity(value: unknown): UserIdentity {
  const record = objectValue(value, "session user");
  return {
    id: nonEmptyStringField(record, "id"),
    email: nonEmptyStringField(record, "email"),
    displayName: nonEmptyStringField(record, "display_name"),
  };
}

function objectValue(value: unknown, description: string): Record<string, unknown> {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    throw new TypeError(`${description} must be an object`);
  }
  return value as Record<string, unknown>;
}

function nonEmptyStringField(record: Record<string, unknown>, field: string): string {
  const value = record[field];
  if (typeof value !== "string" || value.length === 0) {
    throw new TypeError(`${field} must be a non-empty string`);
  }
  return value;
}

function integerField(record: Record<string, unknown>, field: string, minimum: number): number {
  const value = record[field];
  if (!Number.isSafeInteger(value) || (value as number) < minimum) {
    throw new TypeError(`${field} must be an integer of at least ${minimum}`);
  }
  return value as number;
}

function booleanField(record: Record<string, unknown>, field: string): boolean {
  const value = record[field];
  if (typeof value !== "boolean") throw new TypeError(`${field} must be a boolean`);
  return value;
}

function roleField(record: Record<string, unknown>, field: string): Role {
  const value = record[field];
  if (!roles.some((role) => role === value)) {
    throw new TypeError(`${field} must be a Groundspark role`);
  }
  return value as Role;
}
